import type { OperationTypeDefinition } from '@/graph/domain/types';
import type { AddStepFormState } from './types';

/**
 * Build operationTemplate from operation definition metadata (NOT user values).
 * This captures the operation definition snapshot at step creation time,
 * allowing reconstruction of parameter metadata even if catalog changes later.
 * 
 * Structure mirrors backend OperationTemplate with:
 * - parameters: Array of parameter definitions from catalog (no user values)
 * - hideParamsMap: Boolean map of hidden fields (true if required=true)
 * - translationTemplate: Template string for translation preview
 * 
 * @param operationType - Latest OperationTypeDefinition from catalog
 * @param form - Form state (UNUSED - kept for API compatibility)
 * @returns Complete operationTemplate object matching backend schema
 */
export function buildOperationTemplate(
  operationType: OperationTypeDefinition,
  form: AddStepFormState,  // Kept for signature compatibility but unused
) {
  // Build parameters array from operation definition ONLY (no user values)
  const parameters = (operationType.parameters || []).map((paramDef) => {
    return {
      fieldType: paramDef.fieldType || 'text',
      rowCount: paramDef.rowCount ?? null,
      pumpCount: paramDef.pumpCount ?? null,
      rangeMin: paramDef.rangeMin ?? null,
      rangeMax: paramDef.rangeMax ?? null,
      required: paramDef.required || false,
      uniqueAcrossWorkspace: paramDef.uniqueAcrossWorkspace || false,
      allowSpecialCharacters: paramDef.allowSpecialCharacters || false,
      _id: paramDef.id,
      parameterName: paramDef.parameterName,
      parameterTags: paramDef.parameterTags || [],
      order: paramDef.order ?? 0,
      // Include nested structure for group parameters
      ...(paramDef.fieldType === 'group' && paramDef.children
        ? {
            paramValues: (paramDef.children || []).map((child) => ({
              parameterName: child.parameterName,
              fieldType: child.fieldType || 'text',
              required: child.required || false,
              uniqueAcrossWorkspace: child.uniqueAcrossWorkspace || false,
              allowSpecialCharacters: child.allowSpecialCharacters || false,
              units: [],
              parameterTags: child.parameterTags || [],
              rangeMin: child.rangeMin ?? null,
              rangeMax: child.rangeMax ?? null,
              dropDownList: child.dropDownList || [],
              order: child.order ?? 0,
            })),
          }
        : {}),
      // Include table columns for table parameters
      ...(paramDef.fieldType === 'table' && paramDef.tableColumns
        ? { tableColumns: paramDef.tableColumns }
        : {}),
      // Include dropdown list for dropdown parameters
      ...(paramDef.fieldType === 'dropdown' && paramDef.dropDownList
        ? { dropDownList: paramDef.dropDownList }
        : {}),
    };
  });

  // Build hideParamsMap: true if required=true, false otherwise
  const hideParamsMap: Record<string, boolean> = {};
  for (const paramDef of operationType.parameters || []) {
    hideParamsMap[paramDef.parameterName] = !!paramDef.required;
  }

  // Build translationTemplate from operation definition
  const translationTemplate = operationType.translationTemplate || '';

  const result = {
    operationTypeId: operationType.id,
    parameters,
    hideParamsMap,
    translationTemplate,
  };
  return result;
}
